import { useEffect, useState } from 'react';
import { useOpenCv } from 'opencv-react';
import { toolTracker } from '../toolTracker';
import { ibisTool } from '../../helpers/calibration-constants';

const useToolDetection = ({ videoDOM, canvasDOM, perceivedFocalLength }) => {
  const { loaded, cv } = useOpenCv();
  const [shouldCollectToolDistance, setShouldCollectToolDistance] = useState(false);
  const [shouldCalculatePerceivedFocalLength, setShouldCalculatePerceivedFocalLength] = useState(false);
  const [collectedHypotenuses, setCollectedHypotenuses] = useState([]);
  const [calculatedPerceivedFocalLength, setCalculatedPerceivedFocalLength] = useState(0);
  const [alertChange, setAlertChange] = useState(false);
  const [toolDistance, setToolDistance] = useState(0);
  const [collectedDistances, setCollectedDistances] = useState([]);

  useEffect(() => {
    if (loaded && cv && ibisTool && videoDOM && canvasDOM) {
      toolTracker.init(cv, ibisTool, videoDOM, canvasDOM, perceivedFocalLength);
    }
  }, [loaded, cv, videoDOM, canvasDOM, perceivedFocalLength]);

  useEffect(() => {
    const getHypotenuse = () => {
      const sumOfHypotenuses = collectedHypotenuses.reduce((hypotenuseSum, currentHypotenuse, currentIndex) => {
        if (
          currentHypotenuse < ibisTool.thresholdMinimumSizePixels ||
          currentHypotenuse > ibisTool.thresholdMaximumSizePixels ||
          hypotenuseSum <= 0
        ) {
          return 0;
        }

        if (currentIndex === 0) return hypotenuseSum;

        const hypotenuseDifference =
          Math.max(collectedHypotenuses[currentIndex - 1], currentHypotenuse) - Math.min(collectedHypotenuses[currentIndex - 1], currentHypotenuse);

        if (hypotenuseDifference > ibisTool.thresholdMaximumIntervalPixels) return 0;

        return hypotenuseSum + currentHypotenuse;
      }, collectedHypotenuses[0]);

      return sumOfHypotenuses / collectedHypotenuses.length;
    };

    if (shouldCalculatePerceivedFocalLength === true) {
      (async () => {
        if (collectedHypotenuses.length >= ibisTool.minimumValidCollections) {
          setCalculatedPerceivedFocalLength(
            Math.round((getHypotenuse() * ibisTool.distanceCalibrationTriangleInMm) / ibisTool.distanceFrontTrianglesInMm),
          );
          setShouldCalculatePerceivedFocalLength(false);
          setCollectedHypotenuses([]);
          setAlertChange(true);
        } else {
          await new Promise((resolve) => {
            setTimeout(() => {
              resolve();
            }, ibisTool.waitingTimeBetweenCollections);
          });
          setCollectedHypotenuses([...collectedHypotenuses, toolTracker.extractHypotenuse()]);
        }
      })();
    }
  }, [shouldCalculatePerceivedFocalLength, collectedHypotenuses]);

  useEffect(() => {
    const getDistance = () => {
      const sumOfDistances = collectedDistances.reduce((distancesSum, currentDistance, currentIndex) => {
        if (
          currentDistance < ibisTool.distanceThresholdMinimumSizePixels ||
          currentDistance > ibisTool.distanceThresholdMaximumSizePixels ||
          distancesSum <= 0
        ) {
          return 0;
        }

        if (currentIndex === 0) return distancesSum;

        const distanceDifference =
          Math.max(collectedDistances[currentIndex - 1], currentDistance) - Math.min(collectedDistances[currentIndex - 1], currentDistance);

        if (distanceDifference > ibisTool.distanceThresholdMaximumIntervalPixels) return 0;

        return distancesSum + currentDistance;
      }, collectedDistances[0]);

      return sumOfDistances / collectedDistances.length;
    };

    if (shouldCollectToolDistance === true) {
      (async () => {
        if (collectedDistances.length) {
          setToolDistance(Math.round(getDistance()));
          setCollectedDistances([]);
        } else {
          await new Promise((resolve) => {
            setTimeout(() => {
              resolve();
            }, ibisTool.waitingTimeBetweenCollections);
          });
          setCollectedDistances([...collectedDistances, toolTracker.extractDistance()]);
        }
      })();
    } else if (collectedDistances.length) {
      setCollectedDistances([]);
    }
  }, [collectedDistances, shouldCollectToolDistance]);

  return {
    setShouldCalculatePerceivedFocalLength,
    setAlertChange,
    alertChange,
    calculatedPerceivedFocalLength,
    setShouldCollectToolDistance,
    toolDistance,
  };
};

export default useToolDetection;
